/**
 * Names for user ids — one lookup shared by every surface that shows a person
 * by id (the item list's Owner and Created By columns, the custom report's
 * table, a report segmented by owner).
 *
 * The item list used to do this inline, and the custom report page did not do
 * it at all, which is why the same rows showed names on one page and dashes
 * on the other. One hook, one answer.
 *
 * Ids may be `users.id` or `users.external_id`; both are looked up and both
 * keys land in the map. Non-UUID ids (seeded display names) are skipped.
 */

import { useEffect, useState } from 'react';
import { getSafeSupabaseClient } from '../lib/supabaseSync';

const UUID_PREFIX = /^[0-9a-f]{8}-/i;

export function useUserNameMap(ids: ReadonlyArray<string | null | undefined>): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>({});
  const wanted = [...new Set(ids.filter((id): id is string => !!id && UUID_PREFIX.test(id)))].sort();
  const key = wanted.join(',');

  useEffect(() => {
    if (wanted.length === 0) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      try {
        const { data: byId } = await supabase.from('users').select('id, external_id, name, email').in('id', wanted);
        const { data: byExtId } = await supabase.from('users').select('id, external_id, name, email').in('external_id', wanted);
        for (const u of [...(byId || []), ...(byExtId || [])]) {
          const name = u.name || u.email?.split('@')[0] || 'Unknown';
          next[u.id] = name;
          if (u.external_id) next[u.external_id] = name;
        }
        if (!cancelled) setMap(next);
      } catch { /* a failed lookup leaves ids as ids */ }
    })();
    return () => { cancelled = true; };
    // `key` is the sorted id list; the array identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return map;
}
